import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, HostListener, inject} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {DatePickerOptions} from '../../../../model/date-picker-options';
import { Bind } from 'primeng/bind';
import { DatePicker } from 'primeng/datepicker';
import { unblockBodyScroll } from 'primeng/dom';
import { FormsModule } from '@angular/forms';

/**
 * How long PrimeNG gets to tear its own mask down before we step in. Comfortably
 * longer than the mask transition, so a working animation always wins the race
 * and this stays a no-op.
 */
const SCROLL_LOCK_GRACE_MS = 500;

@Component({
    selector: 'myabvv-date-filter',
    standalone: true,
    templateUrl: './date-filter.component.html',
    imports: [Bind, DatePicker, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateFilterComponent implements OnInit, OnDestroy {

  private static nextId = 0;

  @Input() datePickerOptionsDate: DatePickerOptions;
  @Input() readonly = false;
  @Input() date: Date;
  @Input() label: string;
  @Output() changeDateEvent = new EventEmitter<Date>();

  isMobile = false;
  inputId = `date-filter-${DateFilterComponent.nextId++}`;

  private readonly document = inject(DOCUMENT);
  private scrollLockTimer?: ReturnType<typeof setTimeout>;

  constructor() { }

  ngOnInit() {
    this.checkMobile();
  }

  ngOnDestroy() {
    clearTimeout(this.scrollLockTimer);
  }

  @HostListener('window:resize')
  onResize() {
    this.checkMobile();
  }

  private checkMobile() {
    this.isMobile = window.innerWidth <= 759;
  }

  onChangeDate() {
    return this.changeDateEvent.emit(this.date);
  }

  /** Arm the scroll-lock safety net once the picker has closed. */
  onPickerClosed() {
    clearTimeout(this.scrollLockTimer);
    this.scrollLockTimer = setTimeout(() => this.releaseStuckScrollLock(), SCROLL_LOCK_GRACE_MS);
  }

  /**
   * Release a body scroll lock PrimeNG failed to clean up.
   *
   * With touchUI on (any viewport <= 759px, so every phone) the datepicker opens
   * a modal mask and calls blockBodyScroll(), which puts `p-overflow-hidden` on
   * <body>. It only ever releases that from destroyMask(), which it reaches via
   * an `animationend` listener on the mask. On close it tags the mask
   * `p-overlay-mask-leave` -- but the stylesheet only animates
   * `p-overlay-mask-leave-active`, so no animation runs, `animationend` never
   * fires, destroyMask() never executes, and the page stops scrolling for good
   * after the first date pick (PrimeNG 21.1.7). The stale mask also stays in the
   * DOM on top of everything, so drop it too.
   */
  private releaseStuckScrollLock() {
    const body = this.document.body;

    body.querySelectorAll('.p-datepicker-mask-scrollblocker.p-overlay-mask-leave')
      .forEach(mask => mask.remove());

    // A dialog or drawer may still legitimately hold the lock -- leave it be.
    if (body.querySelector('.p-overlay-mask')) {
      return;
    }

    if (body.classList.contains('p-overflow-hidden')) {
      unblockBodyScroll();
    }
  }

}
